import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BrokenImageOutlinedIcon from '@mui/icons-material/BrokenImageOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CropFreeIcon from '@mui/icons-material/CropFree';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { SimilarityBar } from '../../components/common/SimilarityBar';
import { CropImage } from '../../components/common/CropImage';
import { DecisionBadge, DecisionBadgeConfig } from '../../components/common/DecisionBadge';
import { DesignPickerPanel } from '../../components/common/DesignPickerPanel';
import {
  ForgeCandidateDesign,
  ForgeUncertainClassificationPayload,
} from '../../types';
import { filterCandidates, MAX_NUMBER_KEYS } from '../../utils/filterCandidates';

type DecisionType = 'non-front' | 'front' | 'unknown_product' | 'bad_image';

interface CropDecision {
  type: DecisionType;
  designId?: string;
}

interface UncertainClassificationUIProps {
  payload: ForgeUncertainClassificationPayload;
  onSelect: (data: { decisions: Record<string, CropDecision> }) => void;
}

export const UncertainClassificationUI: React.FC<UncertainClassificationUIProps> = ({
  payload,
  onSelect,
}) => {
  const { crops, candidateDesigns, threshold } = payload;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [decisions, setDecisions] = useState<Record<string, CropDecision>>({});
  const [showDesignPicker, setShowDesignPicker] = useState(false);
  const [selectedDesignIdx, setSelectedDesignIdx] = useState(0);
  const currentCrop = crops[currentIndex];
  const currentDecision = currentCrop ? decisions[currentCrop.id] : undefined;
  const completedCount = Object.keys(decisions).length;

  // Report decisions upstream
  useEffect(() => {
    onSelect({ decisions });
  }, [decisions, onSelect]);

  const filteredCandidates = useMemo(
    () => filterCandidates(candidateDesigns, currentCrop?.upc),
    [currentCrop, candidateDesigns]
  );

  const setDecision = useCallback(
    (cropId: string, decision: CropDecision) => {
      setDecisions((prev) => ({ ...prev, [cropId]: decision }));
    },
    []
  );

  const advanceToNext = useCallback(
    (skipId: string) => {
      const nextIdx = crops.findIndex((c, i) => i > currentIndex && !decisions[c.id]);
      if (nextIdx !== -1) {
        setCurrentIndex(nextIdx);
      } else {
        const wrapIdx = crops.findIndex((c) => !decisions[c.id] && c.id !== skipId);
        if (wrapIdx !== -1) setCurrentIndex(wrapIdx);
      }
    },
    [currentIndex, crops, decisions]
  );

  const handleNonFront = useCallback(() => {
    if (!currentCrop) return;
    setDecision(currentCrop.id, { type: 'non-front' });
    setShowDesignPicker(false);
    advanceToNext(currentCrop.id);
  }, [currentCrop, setDecision, advanceToNext]);

  const handleFront = useCallback(() => {
    if (!currentCrop) return;
    setShowDesignPicker(true);
    setSelectedDesignIdx(0);
  }, [currentCrop]);

  const handleSelectDesign = useCallback(
    (design: ForgeCandidateDesign) => {
      if (!currentCrop) return;
      setDecision(currentCrop.id, { type: 'front', designId: design.id });
      setShowDesignPicker(false);
      advanceToNext(currentCrop.id);
    },
    [currentCrop, setDecision, advanceToNext]
  );

  const handleUnknownProduct = useCallback(() => {
    if (!currentCrop) return;
    setDecision(currentCrop.id, { type: 'unknown_product' });
    setShowDesignPicker(false);
    advanceToNext(currentCrop.id);
  }, [currentCrop, setDecision, advanceToNext]);

  const handleBadImage = useCallback(() => {
    if (!currentCrop) return;
    setDecision(currentCrop.id, { type: 'bad_image' });
    setShowDesignPicker(false);
    advanceToNext(currentCrop.id);
  }, [currentCrop, setDecision, advanceToNext]);

  const navigateUp = useCallback(() => {
    if (showDesignPicker) {
      setSelectedDesignIdx((prev) => Math.max(0, prev - 1));
    } else {
      setCurrentIndex((prev) => Math.max(0, prev - 1));
      setShowDesignPicker(false);
    }
  }, [showDesignPicker]);

  const navigateDown = useCallback(() => {
    if (showDesignPicker) {
      setSelectedDesignIdx((prev) => Math.min(filteredCandidates.length - 1, prev + 1));
    } else {
      setCurrentIndex((prev) => Math.min(crops.length - 1, prev + 1));
      setShowDesignPicker(false);
    }
  }, [showDesignPicker, filteredCandidates.length, crops.length]);

  const handleEscape = useCallback(() => {
    if (showDesignPicker) {
      setShowDesignPicker(false);
    }
  }, [showDesignPicker]);

  // Number key handlers for design selection (1-7)
  const numberHandlers = useMemo(() => {
    const handlers: Record<string, () => void> = {};
    for (let i = 1; i <= MAX_NUMBER_KEYS; i++) {
      const idx = i - 1;
      handlers[String(i)] = () => {
        if (showDesignPicker && filteredCandidates[idx]) {
          handleSelectDesign(filteredCandidates[idx]);
        }
      };
    }
    return handlers;
  }, [showDesignPicker, filteredCandidates, handleSelectDesign]);

  useKeyboardShortcuts({
    n: handleNonFront,
    f: handleFront,
    u: handleUnknownProduct,
    b: handleBadImage,
    arrowup: navigateUp,
    arrowdown: navigateDown,
    escape: handleEscape,
    ...numberHandlers,
  });

  if (!currentCrop) return null;

  return (
    <div style={{ display: 'flex', height: '100%', gap: 0 }}>
      {/* Left: Crop list */}
      <div
        style={{
          width: 260,
          minWidth: 260,
          borderRight: '1px solid var(--border)',
          overflowY: 'auto',
          background: 'var(--surface)',
        }}
      >
        <div
          style={{
            padding: '12px 14px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
            Uncertain Crops
          </span>
          <span
            style={{
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-tertiary)',
            }}
          >
            {completedCount}/{crops.length}
          </span>
        </div>
        <div style={{ padding: '2px 0' }}>
          {crops.map((crop, idx) => {
            const dec = decisions[crop.id];
            const isActive = idx === currentIndex;
            return (
              <div
                key={crop.id}
                onClick={() => {
                  setCurrentIndex(idx);
                  setShowDesignPicker(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 14px',
                  cursor: 'pointer',
                  background: isActive ? 'var(--elevated)' : 'transparent',
                  borderLeft: isActive ? '2px solid var(--accent)' : '2px solid transparent',
                  transition: 'background 80ms ease',
                }}
              >
                <CropImage
                  imageUrl={crop.imageUrl}
                  designColor={crop.designColor}
                  size={36}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {crop.productName}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      fontFamily: 'var(--font-mono)',
                      color: scoreColor(crop.recognitionScore),
                    }}
                  >
                    {(crop.recognitionScore * 100).toFixed(0)}% score
                  </div>
                </div>
                {dec && <DecisionBadge config={DECISION_CONFIGS[dec.type]} />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Center: Current crop detail */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Info bar */}
        <div
          className="task-info-banner"
          style={{ margin: 0, padding: '8px 20px', borderRadius: 0 }}
        >
          <span className="info-banner-id">{currentCrop.id}</span>
          <span className="info-banner-upc">{currentCrop.upc}</span>
          <div className="info-banner-sim">
            <SimilarityBar value={currentCrop.recognitionScore} />
          </div>
          <span className="info-banner-hint">
            Threshold: {(threshold * 100).toFixed(0)}%
          </span>
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            gap: 20,
            padding: 20,
            overflow: 'hidden',
          }}
        >
          {/* Crop image & predictions */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                background: 'var(--elevated)',
                borderRadius: 'var(--radius)',
                border: '1px solid var(--border)',
                padding: 16,
                minHeight: 200,
              }}
            >
              <CropImage
                imageUrl={currentCrop.imageUrl}
                designColor={currentCrop.designColor}
                size={200}
                borderRadius={6}
              />
            </div>

            {/* Predictions */}
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  marginBottom: 8,
                }}
              >
                Recognition Predictions
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {currentCrop.predictions.map((pred, i) => (
                  <div
                    key={pred.designId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '6px 10px',
                      background: i === 0 ? 'var(--elevated)' : 'transparent',
                      borderRadius: 'var(--radius-sm)',
                      border: i === 0 ? '1px solid var(--border)' : '1px solid transparent',
                    }}
                  >
                    <img
                      src={pred.representativeImageUrl}
                      alt={pred.designName}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 4,
                        objectFit: 'cover',
                        border: '1px solid var(--border)',
                      }}
                    />
                    <span
                      style={{
                        flex: 1,
                        fontSize: 12,
                        color: 'var(--text-primary)',
                        fontWeight: i === 0 ? 600 : 400,
                      }}
                    >
                      {pred.designName}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        fontFamily: 'var(--font-mono)',
                        color: scoreColor(pred.confidence),
                        fontWeight: 600,
                      }}
                    >
                      {(pred.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Decision buttons — Non-front and Front only */}
            <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
              <button
                onClick={handleNonFront}
                className="btn-secondary"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  ...(currentDecision?.type === 'non-front'
                    ? {
                        background: 'var(--accent)',
                        color: '#fff',
                        borderColor: 'var(--accent)',
                      }
                    : {}),
                }}
              >
                <CropFreeIcon style={{ width: 16, height: 16 }} />
                Non-front
                <span className="kbd" style={{ marginLeft: 4 }}>N</span>
              </button>
              <button
                onClick={handleFront}
                className="btn-secondary"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  ...(currentDecision?.type === 'front'
                    ? {
                        background: 'var(--positive)',
                        color: '#fff',
                        borderColor: 'var(--positive)',
                      }
                    : {}),
                }}
              >
                <CheckCircleOutlineIcon style={{ width: 16, height: 16 }} />
                Front
                <span className="kbd" style={{ marginLeft: 4 }}>F</span>
              </button>
              <button
                onClick={handleBadImage}
                className="btn-secondary"
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 6,
                  padding: '10px 12px',
                  fontSize: 13,
                  fontWeight: 600,
                  ...(currentDecision?.type === 'bad_image'
                    ? {
                        background: 'var(--danger)',
                        color: '#fff',
                        borderColor: 'var(--danger)',
                      }
                    : {}),
                }}
              >
                <BrokenImageOutlinedIcon style={{ width: 16, height: 16 }} />
                Bad Image
                <span className="kbd" style={{ marginLeft: 4 }}>B</span>
              </button>
            </div>
          </div>

          {/* Right: Design picker (shown when "Front" is selected) */}
          {showDesignPicker && (
            <DesignPickerPanel
              title="Select Design"
              candidates={filteredCandidates}
              selectedIdx={selectedDesignIdx}
              onSelectIdx={setSelectedDesignIdx}
              onPick={handleSelectDesign}
              chosenDesignId={
                currentDecision?.type === 'front' ? currentDecision.designId : undefined
              }
              footer={
                <button
                  onClick={handleUnknownProduct}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    padding: '10px 12px',
                    fontSize: 12,
                    fontWeight: 700,
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid rgba(124,58,237,0.5)',
                    cursor: 'pointer',
                    transition: 'all 100ms ease',
                    ...(currentDecision?.type === 'unknown_product'
                      ? {
                          background: '#7C3AED',
                          color: '#fff',
                          borderColor: '#7C3AED',
                        }
                      : {
                          background: 'rgba(124,58,237,0.1)',
                          color: '#A78BFA',
                        }),
                  }}
                >
                  <HelpOutlineIcon style={{ width: 16, height: 16 }} />
                  Unknown Product
                  <span className="kbd" style={{ marginLeft: 6, fontSize: 10 }}>U</span>
                </button>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ── Helpers ──

const DECISION_CONFIGS: Record<DecisionType, DecisionBadgeConfig> = {
  'non-front': { label: 'NF', bg: 'rgba(107,114,128,0.15)', color: 'var(--text-secondary)' },
  front: { label: 'F', bg: 'rgba(16,185,129,0.15)', color: 'var(--positive)' },
  bad_image: { label: 'B', bg: 'rgba(239,68,68,0.15)', color: 'var(--danger)' },
  unknown_product: { label: 'U', bg: 'rgba(124,58,237,0.15)', color: '#A78BFA' },
};

function scoreColor(score: number): string {
  if (score >= 0.80) return 'var(--positive)';
  if (score >= 0.75) return 'var(--warning)';
  return 'var(--danger)';
}
